import * as signalR from "@microsoft/signalr";
import { DateTime } from "luxon";

import { ErrorTemplateBuilder } from "../../infrastructure/error-template-builder";
import { Enums } from "../helper/enums";
import { ticketEntity } from "../model/entity/ticket-entity";
import { EntityManager } from "./entity-manager";
import ticketApiService from "./ticket-api-service";
import { customStatusEntity } from "../model/entity/custom-status-entity";
import PrototypeInstance from "../helper/prototype-instance";
import { ticketTypeSettingEntity } from "../model/entity/ticket-type-setting-entity";
import { EntitySyncManager } from "../../infrastructure/entity-sync-manager";
import {
  applyClientPortalUnreadStateOnTicketSync,
  type TicketRecentCommentSnapshot,
} from "../entity-service/ticket-entity-service";
import ticketService from "./ticket-service";
import CustomEventEmitter from "../helper/event-emitter";
import { bumpTicketSidebarListRevision } from "../../ticket/ticket-sidebar-list-refresh";

const eventEmitter = CustomEventEmitter.getInstance();
const TICKET_SUMMARY_TAGS_CHANGED_EVENT = "ticket-summary-tags-changed";

/** Payload from the MSP app when a technician is typing on a ticket (hub → client). */
export type TicketTechnicianTypingPayload = {
  ticketId: string;
  technicianId: number;
  isTyping: boolean;
  firstName?: string;
  lastName?: string;
  /** Timeline tab id (TPC id when `currentTabType` is `tpc`). */
  commentTabId?: number;
  currentTabType?: string;
  approvalId?: string;
};

async function fetchRecentCommentForCxTicket(
  ticketId: string,
): Promise<TicketRecentCommentSnapshot | null> {
  if (!ticketId) {
    return null;
  }
  try {
    const snapshot = await ticketService.getRecentCommentForCx(ticketId);
    return snapshot ?? null;
  } catch {
    return null;
  }
}

function parseLastModifiedFromSignalr(
  message: any,
): Record<string, unknown> | null {
  const raw =
    message?.data?.lastModifiedJson ??
    message?.lastModifiedJson ??
    message?.Data?.lastModifiedJson ??
    message?.LastModifiedJson;
  if (raw == null || raw === "") {
    return null;
  }
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function hasEditedTypeInLastModifiedRaw(
  raw: unknown,
  editedType: number,
): boolean {
  if (raw == null) {
    return false;
  }
  const s = String(raw);
  // Handles both plain JSON and escaped JSON strings.
  return (
    s.includes(`"editedType":${editedType}`) ||
    s.includes(`\\"editedType\\":${editedType}`)
  );
}

function isTicketTagEditSignalr(message: any): boolean {
  if (message?.data?.isTicketTag === true || message?.isTicketTag === true) {
    return true;
  }
  // Some payloads omit `isTicketTag` but include tag changes in lastModifiedJson.
  const raw =
    message?.data?.lastModifiedJson ??
    message?.lastModifiedJson ??
    message?.Data?.lastModifiedJson ??
    message?.LastModifiedJson;
  if (hasEditedTypeInLastModifiedRaw(raw, 14)) {
    return true;
  }
  const lm = parseLastModifiedFromSignalr(message);
  return Number(lm?.editedType ?? 0) === 14;
}

function shouldFetchRecentCommentForCx(message: any): boolean {
  // Skip tag-only edits (404 noise). Inbound TPC / CC often omits `lastActionId` on
  // SignalR payloads — fetch cx/recent for all other ticket activity.
  return !isTicketTagEditSignalr(message);
}

function ticketRowWithCxRecentComment<T extends object>(
  result: T,
  recentComment: TicketRecentCommentSnapshot | null,
): T {
  if (recentComment == null) {
    return result;
  }
  const row = result as {
    recentCommentId?: string | null;
  };
  const detailRecentId = String(row.recentCommentId ?? "").trim();
  const cxRecentId = String(recentComment.commentId ?? "").trim();
  const keepDetailRecentId =
    detailRecentId !== "" && cxRecentId !== "" && detailRecentId !== cxRecentId;
  return {
    ...result,
    recentComment,
    ...(keepDetailRecentId || !cxRecentId
      ? {}
      : { recentCommentId: cxRecentId }),
  };
}

function parseJsonValue(raw: unknown): unknown {
  if (typeof raw !== "string") {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/** Unwraps hub payloads that nest the body under message/data. */
function unwrapTicketJoinedBody(message: unknown): unknown {
  let o = parseJsonValue(message);
  if (o == null || typeof o !== "object") {
    return o;
  }
  const record = o as Record<string, unknown>;
  const nested =
    record.message ??
    record.Message ??
    record.data ??
    record.Data ??
    record.payload ??
    record.Payload;
  if (nested == null) {
    return o;
  }
  return parseJsonValue(nested);
}

function parseExplicitIsTyping(
  o: Record<string, unknown>,
): boolean | undefined {
  const raw = o.isTyping ?? o.IsTyping;
  if (raw === undefined) {
    return undefined;
  }
  if (raw === true || raw === "true" || raw === 1) {
    return true;
  }
  if (raw === false || raw === "false" || raw === 0) {
    return false;
  }
  return undefined;
}

/** Resolves typing state from `ticketJoined` payloads; ignores non-typing events (e.g. UserViewed). */
function resolveTicketJoinedTypingState(
  o: Record<string, unknown>,
): boolean | undefined {
  const explicit = parseExplicitIsTyping(o);
  if (explicit !== undefined) {
    return explicit;
  }
  const type = Number(o.type ?? o.Type);
  if (type === Enums.UserViewingSignalRType.UserTyping) {
    return true;
  }
  return undefined;
}

function isTypingTicketJoinedEvent(o: Record<string, unknown>): boolean {
  if (o.isTyping !== undefined || o.IsTyping !== undefined) {
    return true;
  }
  const type = Number(o.type ?? o.Type);
  if (type === Enums.UserViewingSignalRType.UserTyping) {
    return true;
  }
  const eventName = String(
    o.event ?? o.Event ?? o.eventName ?? o.EventName ?? "",
  ).toLowerCase();
  return eventName.includes("typing");
}

/** Tab types where portal clients can see technician typing (hub `currentTabType`). */
const PORTAL_VISIBLE_TYPING_TAB_TYPES = new Set(["public", "tpc", "approval"]);

/** Portal only shows typing for client-visible conversation tabs (not internal MSP tabs). */
function isClientVisibleTabTypingPayload(o: Record<string, unknown>): boolean {
  const tabType = String(
    o.currentTabType ?? o.CurrentTabType ?? "",
  ).toLowerCase();
  if (!tabType) {
    return true;
  }
  if (!PORTAL_VISIBLE_TYPING_TAB_TYPES.has(tabType)) {
    return false;
  }

  const store = PrototypeInstance.globalProperties.$store;

  if (tabType === "tpc") {
    const tpcId = Number(
      o.commentTabId ??
        o.CommentTabId ??
        o.thirdPartyConversationId ??
        o.ThirdPartyConversationId ??
        0,
    );
    if (!Number.isFinite(tpcId) || tpcId <= 0) {
      return false;
    }
    const tpcIds: number[] =
      store?.getters?.["ticketDetailModule/getTpcIds"] ?? [];
    return tpcIds.includes(tpcId);
  }

  if (tabType === "approval") {
    const approvalId = String(
      o.commentTabId ?? o.CommentTabId ?? o.approvalId ?? o.ApprovalId ?? "",
    ).trim();
    if (!approvalId) {
      return false;
    }
    const approvalIds: string[] =
      store?.getters?.["ticketDetailModule/getApprovalIds"] ?? [];
    return approvalIds.includes(approvalId);
  }

  return true;
}

function parseTicketTechnicianTypingMessage(
  message: unknown,
): TicketTechnicianTypingPayload | null {
  const body = unwrapTicketJoinedBody(message);
  if (body == null || typeof body !== "object") {
    return null;
  }

  const o = body as Record<string, unknown>;
  if (!isTypingTicketJoinedEvent(o)) {
    return null;
  }

  const resolvedTyping = resolveTicketJoinedTypingState(o);
  if (resolvedTyping === undefined) {
    return null;
  }
  const isTyping = resolvedTyping;
  if (isTyping && !isClientVisibleTabTypingPayload(o)) {
    return null;
  }

  const ticketId = String(o.ticketId ?? o.TicketId ?? "").trim();
  const technicianId = Number(o.technicianId ?? o.TechnicianId ?? 0);
  if (!ticketId || !Number.isFinite(technicianId) || technicianId <= 0) {
    return null;
  }
  const commentTabIdRaw = o.commentTabId ?? o.CommentTabId;
  const commentTabId =
    commentTabIdRaw != null && commentTabIdRaw !== ""
      ? Number(commentTabIdRaw)
      : undefined;
  const currentTabType = String(
    o.currentTabType ?? o.CurrentTabType ?? "",
  ).toLowerCase();
  const approvalId = String(o.approvalId ?? o.ApprovalId ?? "").trim();

  return {
    ticketId,
    technicianId,
    isTyping,
    firstName: (o.firstName ?? o.FirstName) as string | undefined,
    lastName: (o.lastName ?? o.LastName) as string | undefined,
    commentTabId:
      commentTabId != null && Number.isFinite(commentTabId)
        ? commentTabId
        : undefined,
    currentTabType: currentTabType || undefined,
    approvalId: approvalId || undefined,
  };
}

/** Normalizes hub payloads (direct JSON, nested body, or SendToOthers relay arrays). */
function collectTicketJoinedTypingCandidates(message: unknown): unknown[] {
  const body = unwrapTicketJoinedBody(message);

  if (Array.isArray(body)) {
    if (body.length >= 4) {
      const method = String(body[2] ?? "").toLowerCase();
      if (method === "ticketjoined") {
        const relayed = parseJsonValue(body[body.length - 1]);
        return relayed != null ? [relayed] : [];
      }
    }
    return body;
  }

  if (body != null) {
    return [body];
  }

  return message != null ? [message] : [];
}

function emitTicketTechnicianTypingFromJoined(message: unknown) {
  const emitter = CustomEventEmitter.getInstance();
  const emitParsed = (candidate: unknown) => {
    const payload = parseTicketTechnicianTypingMessage(candidate);
    if (payload) {
      emitter.emit("ticket-technician-typing", payload);
    }
  };

  const body = unwrapTicketJoinedBody(message);

  if (body != null && typeof body === "object" && !Array.isArray(body)) {
    const o = body as Record<string, unknown>;
    const list =
      o.techniciansTyping ??
      o.TechniciansTyping ??
      o.usersTyping ??
      o.UsersTyping;
    if (Array.isArray(list)) {
      const ticketId = String(o.ticketId ?? o.TicketId ?? "").trim();
      for (const item of list) {
        const merged =
          typeof item === "object" && item != null
            ? { ticketId, ...(item as object) }
            : item;
        emitParsed(merged);
      }
      return;
    }
  }

  for (const candidate of collectTicketJoinedTypingCandidates(message)) {
    emitParsed(candidate);
  }
}

export class SignalRTicketService {
  //#region SignalR Connection
  public setTicketConnectionEvents(connection: signalR.HubConnection) {
    connection.on("ticketEntitySync", async (message: any) => {
      try {
        await this.ticketEntitySync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketEntitySync",
            e,
          ),
        );
      }
    });

    connection.on("ticketPrivateCommentAdded", async (message: any) => {
      try {
        const body =
          typeof message === "string" ? JSON.parse(message) : message;
        await this.ticketPrivateCommentAdded(body);
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketPrivateCommentAdded",
            e,
          ),
        );
      }
    });

    connection.on("ticketEntityTickSync", async (message: any) => {
      try {
        await this.ticketEntityTickSync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketEntityTickSync",
            e,
          ),
        );
      }
    });

    connection.on("ticketEntityCollectionSync", async (message: any) => {
      try {
        await this.ticketEntityCollectionSync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketEntityCollectionSync",
            e,
          ),
        );
      }
    });
    connection.on("itemRecovered", async (message: any) => {
      try {
        await this.itemRecovered(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.itemRecovered",
            e,
          ),
        );
      }
    });
    connection.on("ticketCustomStatusSync", async (message: any) => {
      try {
        await this.customStatusEntitySync(JSON.parse(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketCustomStatusSync",
            e,
          ),
        );
      }
    });

    connection.on("ticketTypeSettingsSync", async (message: any) => {
      try {
        const parsed = parseJsonValue(message);
        const body =
          parsed != null && typeof parsed === "object"
            ? (parsed as Record<string, unknown>)
            : null;
        if (!body) {
          return;
        }

        await ticketTypeSettingEntity.insertOrUpdate({ data: body });

        // When enabled, the list of ticket types may need a refresh for UI to reflect newly-available types.
        if (Boolean((body as any).isEnabled)) {
          await EntitySyncManager.fillTicketTypes();
        }
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketTypeSettingsSync",
            e,
          ),
        );
      }
    });

    /**
     * Technician typing on `Ticket_{serviceProviderId}` (joined in joinSignalRGroups).
     * Hub client method from the MSP app: ticketJoined.
     */
    const onTicketJoined = (message: unknown) => {
      try {
        emitTicketTechnicianTypingFromJoined(message);
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRTicketService.ticketJoined",
            e,
          ),
        );
      }
    };
    connection.on("ticketJoined", onTicketJoined);
    connection.on("TicketJoined", onTicketJoined);
  }
  //#endregion

  //#region Functions

  public async ticketEntitySync(message: any) {
    try {
      let contactId: number =
        PrototypeInstance.globalProperties.$store.getters[
          "ContactModule/getContactId"
        ];
      let clientId: number | null =
        PrototypeInstance.globalProperties.$store.getters[
          "ContactModule/getClientId"
        ];

      const ticketId = String(message.entityId ?? "");
      const isTagEdit = isTicketTagEditSignalr(message);
      const fetchRecent = shouldFetchRecentCommentForCx(message);
      const [result, recentComment] = await Promise.all([
        ticketApiService.getTicketDetailByIdForSignalR({
          ticketId: message.entityId,
          tick: message.tick,
        }) as Promise<ticketEntity>,
        fetchRecent
          ? fetchRecentCommentForCxTicket(ticketId)
          : Promise.resolve(null),
      ]);

      if (result) {
        let hasAllTicketsTag: boolean =
          PrototypeInstance.globalProperties.$store.getters[
            "ContactModule/getHasAllTicketsTag"
          ];
        if (
          (hasAllTicketsTag ||
            result.contactId == contactId ||
            result.secondaryContacts == "[" + contactId + "]" ||
            result.secondaryContacts?.includes("," + contactId + ",") ||
            result.secondaryContacts?.startsWith("[" + contactId + ",") ||
            result.secondaryContacts?.endsWith("," + contactId + "]")) &&
          result.clientId == clientId &&
          result.ticketStatusId != Enums.TicketStatus.Merged
        ) {
          const store = PrototypeInstance.globalProperties.$store;
          const selectedTicketId = store.getters[
            "ticketDetailModule/getTicketId"
          ] as string;
          const viewerContactId = store.getters[
            "ContactModule/getContactId"
          ] as number;
          const existingTicket = ticketEntity.find(String(result.ticketId));
          const row = ticketRowWithCxRecentComment(result, recentComment);
          applyClientPortalUnreadStateOnTicketSync(
            row,
            selectedTicketId,
            existingTicket?.isUnread,
            viewerContactId,
          );

          ticketEntity.insertOrUpdate({ data: row });
          bumpTicketSidebarListRevision();

          if (isTagEdit) {
            eventEmitter.emit(TICKET_SUMMARY_TAGS_CHANGED_EVENT, {
              ticketId,
              tick: message.tick,
            });
          }
        } else {
          ticketEntity.delete((ticket: any) => {
            return ticket.ticketId == message.entityId;
          });
        }
      } else {
        ticketEntity.delete((ticket: any) => {
          return ticket.ticketId == message.entityId;
        });
      }
    } catch (e) {
      ticketEntity.delete((ticket: any) => {
        return ticket.ticketId == message.entityId;
      });
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.ticketEntitySync",
          e,
        ),
      );
    }
  }

  public async ticketPrivateCommentAdded(message: any) {
    try {
      const store = PrototypeInstance.globalProperties.$store;
      const selectedTicketId = store.getters[
        "ticketDetailModule/getTicketId"
      ] as string;
      const viewerContactId = store.getters[
        "ContactModule/getContactId"
      ] as number;
      const ticketId = String(message.entityId ?? "");
      const existingTicket = ticketEntity.find(ticketId);
      const updatedOn = DateTime.fromISO(message.updatedOn, {
        zone: "utc",
      }).toISO();

      const recentComment = shouldFetchRecentCommentForCx(message)
        ? await fetchRecentCommentForCxTicket(ticketId)
        : null;

      const data = ticketRowWithCxRecentComment(
        {
          ticketId,
          recentCommentId:
            existingTicket?.recentCommentId ?? message.commentId ?? null,
          recentComment: existingTicket?.recentComment ?? null,
          lastModifiedJson: message.lastModifiedJson,
          mentionedTechnicians: message.mentionedTechnicians,
          lastActionId: message.lastActionId,
          state: message.state,
          updatedOn,
          tick: message.tick,
          updateTicketCount: false,
          createdByContactId: existingTicket?.createdByContactId,
          createdByTechnicianId: existingTicket?.createdByTechnicianId,
          contactId: existingTicket?.contactId,
          createdOn: existingTicket?.createdOn,
          isUnread: existingTicket?.isUnread === true,
        },
        recentComment,
      ) as Record<string, unknown>;

      const cxRecentId = String(
        (data.recentComment as TicketRecentCommentSnapshot | null)?.commentId ??
          "",
      ).trim();
      const signalCommentId = String(message.commentId ?? "").trim();
      if (cxRecentId !== "") {
        data.recentCommentId = cxRecentId;
      } else if (signalCommentId !== "") {
        data.recentCommentId = signalCommentId;
      }

      applyClientPortalUnreadStateOnTicketSync(
        data,
        selectedTicketId,
        existingTicket?.isUnread,
        viewerContactId,
      );

      await EntityManager.updateSingleLocalEntityBySyntax(
        message,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.TicketPrivateCommentAdded
        ],
        "ticketId",
        message.entityId,
        "",
        data,
      );
      bumpTicketSidebarListRevision();
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.ticketPrivateCommentAdded",
          e,
        ),
      );
    }
  }

  public async ticketEntityTickSync(_message?: any) {
    try {
      const latestTick = ticketEntity.query().max("tick");
      let hasAllTicketsTag: boolean =
        PrototypeInstance.globalProperties.$store.getters[
          "ContactModule/getHasAllTicketsTag"
        ];
      let clientId: number | null =
        PrototypeInstance.globalProperties.$store.getters[
          "ContactModule/getClientId"
        ];
      let clientIdValue: number = clientId == null ? 0 : clientId;
      const obj = {
        tick: latestTick,
        clientId: clientIdValue,
        hasAllTicketsTag: hasAllTicketsTag,
      };
      const [result] = await EntityManager.getOnlyAPCallForEntity(
        { eventId: Enums.StateEntitySync.InsertOrUpdate },
        Enums.EntitySyncManager[Enums.EntitySyncManager.CheckTicketTick],
        obj,
      );
      if (result && result.tickets) {
        const store = PrototypeInstance.globalProperties.$store;
        const selectedTicketId = store.getters[
          "ticketDetailModule/getTicketId"
        ] as string;
        const viewerContactId = store.getters[
          "ContactModule/getContactId"
        ] as number;
        const tickets = (result.tickets as any[]).map((ticket: any) => {
          const row = { ...ticket };
          const existing = ticketEntity.find(String(row.ticketId ?? ""));
          applyClientPortalUnreadStateOnTicketSync(
            row,
            selectedTicketId,
            existing?.isUnread,
            viewerContactId,
          );
          return row;
        });
        await ticketEntity.insertOrUpdate({ data: tickets });
        bumpTicketSidebarListRevision();
      }
      if (result && result.ticketsToDelete) {
        ticketEntity.delete((ticket: any) => {
          return result.ticketsToDelete.includes(ticket.ticketId);
        });
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.ticketEntityTickSync",
          e,
        ),
      );
    }
  }

  public async itemRecovered(message: any) {
    try {
      if (message.itemType == Enums.RecoveredItem.Ticket) {
        EntityManager.deleteFromEntity(
          message.itemId,
          Enums.SignalRMessageType[
            Enums.SignalRMessageType.DeletedTicketEntity
          ],
        );
        try {
          let contactId: number =
            PrototypeInstance.globalProperties.$store.getters[
              "ContactModule/getContactId"
            ];
          let clientId: number | null =
            PrototypeInstance.globalProperties.$store.getters[
              "ContactModule/getClientId"
            ];
          let hasAllTicketsTag: boolean =
            PrototypeInstance.globalProperties.$store.getters[
              "ContactModule/getHasAllTicketsTag"
            ];

          const recoveredTicketId = String(message.itemId ?? "");
          const [result, recentComment] = await Promise.all([
            ticketApiService.getTicketDetailByIdForSignalR({
              ticketId: message.itemId,
              tick: message.tick,
            }) as Promise<ticketEntity>,
            fetchRecentCommentForCxTicket(recoveredTicketId),
          ]);
          if (result) {
            if (
              (hasAllTicketsTag || result.contactId == contactId) &&
              result.clientId == clientId
            ) {
              const store = PrototypeInstance.globalProperties.$store;
              const selectedTicketId = store.getters[
                "ticketDetailModule/getTicketId"
              ] as string;
              const viewerContactId = store.getters[
                "ContactModule/getContactId"
              ] as number;
              const existingTicket = ticketEntity.find(String(result.ticketId));
              const row = ticketRowWithCxRecentComment(result, recentComment);
              applyClientPortalUnreadStateOnTicketSync(
                row,
                selectedTicketId,
                existingTicket?.isUnread,
                viewerContactId,
              );
              ticketEntity.insertOrUpdate({ data: row });
              bumpTicketSidebarListRevision();
            } else {
              ticketEntity.delete((ticket: any) => {
                return ticket.ticketId == message.entityId;
              });
            }
          } else {
            ticketEntity.delete((ticket: any) => {
              return ticket.ticketId == message.entityId;
            });
          }
        } catch (e) {
          ticketEntity.delete((ticket: any) => {
            return ticket.ticketId == message.entityId;
          });
          throw new Error(
            ErrorTemplateBuilder.getCatchText(
              "SignalRTicketService.ticketEntitySync",
              e,
            ),
          );
        }
      } else if (message.itemType == Enums.RecoveredItem.Contact) {
        EntityManager.deleteFromEntity(
          message.itemId,
          Enums.SignalRMessageType[
            Enums.SignalRMessageType.InactiveContactEntity
          ],
        );
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.itemRecovered",
          e,
        ),
      );
    }
  }

  public async ticketSetting(message: any) {
    try {
      await EntityManager.updateEntity(
        message,
        Enums.SignalRMessageType[
          Enums.SignalRMessageType.FillMSPTicketSettings
        ],
        "",
        "",
      );
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.ticketSetting",
          e,
        ),
      );
    }
  }

  public async ticketEntityCollectionSync(message: any) {
    try {
      if (message && message.entityId.length > 0) {
        const latestTick = ticketEntity.query().max("tick");
        let hasAllTicketsTag: boolean =
          PrototypeInstance.globalProperties.$store.getters[
            "ContactModule/getHasAllTicketsTag"
          ];
        let clientId: number | null =
          PrototypeInstance.globalProperties.$store.getters[
            "ContactModule/getClientId"
          ];
        let clientIdValue: number = clientId == null ? 0 : clientId;
        const obj = {
          tick: latestTick,
          clientId: clientIdValue,
          hasAllTicketsTag: hasAllTicketsTag,
        };
        const [result] = await EntityManager.getOnlyAPCallForEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.CheckTicketTick],
          obj,
        );
        if (result && result.tickets) {
          await ticketEntity.insertOrUpdate({ data: result.tickets });
        }
        if (result && result.ticketsToDelete) {
          ticketEntity.delete((ticket: any) => {
            return result.ticketsToDelete.includes(ticket.ticketId);
          });
        }
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.ticketEntityCollectionSync",
          e,
        ),
      );
    }
  }

  public async customStatusEntitySync(message: any) {
    try {
      if (message && message.eventId == 1) {
        await customStatusEntity.insertOrUpdate({ data: message.data });
      } else if (message && message.eventId == 1) {
        await customStatusEntity.delete((customStatus: any) => {
          return customStatus.ticketStatusId == message.data.ticketStatusId;
        });
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRTicketService.customStatusEntitySync",
          e,
        ),
      );
    }
  }
}
